import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { cn } from '@immo/ui-kit';
import { type MeResponse } from '@immo/api-contract';
import { membershipForWorkspace } from '@/lib/auth/me-membership';
import { formatDisplayName } from '@/lib/auth/display-name';
import { useWorkspaceRoutes } from '@/lib/workspace-path';
import { useTranslations } from '@/i18n/use-translations';
import { MemberAvatar } from '@/components/member-avatar';

type Props = {
  readonly me: MeResponse;
  // The active workspace (resolved from the URL), so the avatar and links resolve
  // against the workspace being viewed — not `me.organisations[0]`, which for a
  // member of several workspaces need not be the current one.
  readonly workspaceId: string;
  readonly collapsed?: boolean;
};

export function UserMenu({ me, workspaceId, collapsed = false }: Props) {
  const navigate = useNavigate();
  const routes = useWorkspaceRoutes();
  const t = useTranslations('pages.shell.user-menu');
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ bottom: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || triggerRef.current === null) return;

    const rect = triggerRef.current.getBoundingClientRect();

    setPosition({ bottom: window.innerHeight - rect.top + 4, left: rect.left });
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const inTrigger = triggerRef.current !== null && triggerRef.current.contains(target);
      const inMenu = menuRef.current !== null && menuRef.current.contains(target);

      if (!inTrigger && !inMenu) setOpen(false);
    };

    document.addEventListener('mousedown', onClick);

    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  useEffect(() => {
    if (open && position !== null) menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
  }, [open, position]);

  const closeAndReturnFocus = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closeAndReturnFocus();

      return;
    }

    if (event.key === 'Tab') {
      closeAndReturnFocus();

      return;
    }

    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;

    event.preventDefault();
    const items = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]'));
    const current = items.indexOf(document.activeElement as HTMLButtonElement);
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? items.length - 1
          : (current + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
    items[next]?.focus();
  };

  const displayName = formatDisplayName(me);
  const membership = membershipForWorkspace(me, workspaceId);
  const avatarMediaId = membership?.avatarMediaId ?? null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={displayName}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          'flex w-full items-center rounded-md py-1.5 text-left text-base text-text-primary transition-colors duration-micro ease-out hover:bg-hover-bg focus-visible:outline-none focus-visible:shadow-focus',
          collapsed ? 'justify-center px-0' : 'gap-2 px-2',
        )}
      >
        <MemberAvatar
          workspaceId={workspaceId}
          avatarMediaId={avatarMediaId}
          displayName={displayName}
          seed={me.user.email}
          size="sm"
        />
        {!collapsed && <span className="min-w-0 flex-1 truncate text-base-sm font-medium">{displayName}</span>}
      </button>
      {open &&
        position !== null &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label={displayName}
            onKeyDown={onMenuKeyDown}
            style={{ bottom: position.bottom, left: position.left }}
            className="fixed z-50 w-56 overflow-hidden rounded-lg border border-border bg-surface shadow-md"
          >
            <div className="border-b border-border px-3 py-2">
              <div className="text-base font-medium text-text-primary">{displayName}</div>
              <div className="truncate text-sm text-text-tertiary">{me.user.email}</div>
            </div>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                navigate(routes.settings(), { viewTransition: true });
              }}
              className="block w-full rounded-md px-3 py-2 text-left text-base text-text-primary transition-colors duration-micro ease-out hover:bg-hover-bg focus-visible:outline-none focus-visible:bg-hover-bg focus-visible:shadow-focus-row"
            >
              {t('settings')}
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                navigate('/sign-out');
              }}
              className="block w-full rounded-md px-3 py-2 text-left text-base text-text-primary transition-colors duration-micro ease-out hover:bg-hover-bg focus-visible:outline-none focus-visible:bg-hover-bg focus-visible:shadow-focus-row"
            >
              {t('sign-out')}
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}
